import { useEffect, useState } from "react";
import { api } from "../api.js";

interface Translation {
  languageTag: string;
  status: string;
}
interface RecipeRow {
  id: string;
  titleSv: string;
  createdAt: string;
  translations: Translation[];
}
interface Overview {
  languages: string[];
  recipes: RecipeRow[];
}

const primary = (t: string) => t.split("-")[0] ?? t;
const shortLang = (t: string) => primary(t).toUpperCase();

export function TranslationsPage() {
  const [data, setData] = useState<Overview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = () => {
    api<Overview>("/admin/v1/translations")
      .then(setData)
      .catch((e) => setError(String(e.message)));
  };
  useEffect(load, []);

  const retry = async (recipeId: string, languageTag: string) => {
    const key = recipeId + languageTag;
    setBusy(key);
    setError(null);
    try {
      await api(`/admin/v1/translations/${recipeId}/retry`, {
        method: "POST",
        body: { languageTag },
      });
      // Ge workern en stund att köra klart, ladda sedan om översikten.
      setTimeout(load, 2000);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div>
      <h2>Översättningar</h2>
      <p className="muted">
        Community-recept och deras översättningar. Grön ✓ = publicerad (live för användare), gul ~ =
        behöver granskas (verifieringen föll), grå – = saknas. Klicka en ruta för att (om)beställa
        översättning. Godkänner du ett recept i Moderering köas och auto-publiceras alla språk som
        klarar verifieringen.
      </p>
      {error && <div className="error-text">{error}</div>}
      {!data && <div className="panel">Laddar…</div>}
      {data && data.recipes.length === 0 && (
        <div className="panel">Inga publicerade community-recept än.</div>
      )}
      {data && data.recipes.length > 0 && (
        <div className="panel" style={{ overflowX: "auto" }}>
          <table style={{ borderCollapse: "collapse", width: "100%" }}>
            <thead>
              <tr>
                <th style={{ textAlign: "left", padding: 6 }}>Recept</th>
                {data.languages.map((l) => (
                  <th key={l} style={{ padding: 6, fontSize: 12 }} title={l}>
                    {shortLang(l)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.recipes.map((r) => (
                <tr key={r.id} style={{ borderTop: "1px solid #eee" }}>
                  <td style={{ padding: 6 }}>{r.titleSv}</td>
                  {data.languages.map((l) => {
                    const tr = r.translations.find((t) => primary(t.languageTag) === primary(l));
                    const done = tr?.status === "published";
                    const bg = done ? "#1f7a1f" : tr ? "#b8860b" : "#c4c4c4";
                    const label = done ? "✓" : tr ? "~" : "–";
                    const key = r.id + l;
                    return (
                      <td key={l} style={{ padding: 4, textAlign: "center" }}>
                        <button
                          title={
                            tr
                              ? `${tr.status} – klicka för att översätta om`
                              : "saknas – klicka för att översätta"
                          }
                          disabled={busy === key}
                          onClick={() => retry(r.id, l)}
                          style={{
                            background: bg,
                            color: "white",
                            border: "none",
                            borderRadius: 4,
                            minWidth: 28,
                            height: 24,
                            cursor: "pointer",
                          }}
                        >
                          {busy === key ? "…" : label}
                        </button>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
